import { request } from '@/utils/http'

// ==================== 类型声明 ====================

/** 订阅单条记录 */
export interface SubscriptionVO {
  id: number | string
  polCode: string
  podCode: string
  containerTypes: string[]
  carrierCodes: string[]
  enabled: boolean
  dateCreated: string
}

/** 订阅列表分页响应 */
export interface SubscriptionPageResponse {
  records: SubscriptionVO[]
  total: number
  size: number
}

/** 新增/编辑订阅表单 */
export interface SubscriptionForm {
  id?: number | string | null
  polCode: string
  podCode: string
  containerTypes: string[]
  carrierCodes: string[]
}

/** 批量新增订阅参数 */
export interface BatchSubscriptionRequest {
  polCodes: string[]
  podCodes: string[]
  containerTypes?: string[]
  carrierCodes?: string[]
}

/** 导入订阅行数据 */
export interface ImportSubscriptionRow {
  polCode: string
  podCode: string
  carrierCodes?: string[] | null
  containerTypes?: string[] | null
}

/** 全局推送配置 */
export interface PushConfigVO {
  pushChannel: number
  emails: string[]
  pushTimeEarly: number
  pushTimeLate: number
  httpEndpoint?: string
  httpSecret?: string
  exportFieldKeys?: string[]
}

/** 今日运价记录 */
export interface DailyRateVO {
  id: string | number
  polCode: string
  podCode: string
  carrierCode: string
  containerType: string
  basePrice: number
  totalPrice: number
  currency: string
  etd: string
  eta: string
  transitDays: number
  vesselName: string
  voyage: string
  routeCode: string
  spaceStatus: number
  isDirect: number
  rateUpdatedAt: string
}

/** 今日运价分页查询请求 */
export interface DailyRateQueryPageRequest {
  pageNum: number
  pageSize: number
  pushTime?: number
  polCode?: string
  podCode?: string
}

/** 今日运价分页响应 */
export interface DailyRatePageResponse {
  records: DailyRateVO[]
  total: number
  size: number
}

// ==================== API 接口方法 ====================

const BASE_URL = '/price/query/schedule/daily-push'

/**
 * 分页查询每日推送配置订阅列表
 */
export function pageSubscriptions(params: { pageNum: number; pageSize: number }): Promise<SubscriptionPageResponse> {
  return request.post<SubscriptionPageResponse>(`${BASE_URL}/subscriptions/page`, params)
}

/**
 * 新增配置订阅
 */
export function createSubscription(data: SubscriptionForm): Promise<void> {
  return request.post<void>(`${BASE_URL}/subscriptions`, data)
}

/**
 * 编辑配置订阅
 */
export function updateSubscription(id: number | string, data: SubscriptionForm): Promise<void> {
  return request.put<void>(`${BASE_URL}/subscriptions/${id}`, data)
}

/**
 * 删除配置订阅
 */
export function deleteSubscription(id: number | string): Promise<void> {
  return request.delete<void>(`${BASE_URL}/subscriptions/${id}`)
}

/**
 * 批量新增配置订阅
 */
export function batchCreateSubscriptions(data: BatchSubscriptionRequest): Promise<void> {
  return request.post<void>(`${BASE_URL}/subscriptions/batch`, data)
}

/**
 * 导入配置订阅
 */
export function importSubscriptions(rows: ImportSubscriptionRow[]): Promise<void> {
  return request.post<void>(`${BASE_URL}/subscriptions/import`, rows)
}

/**
 * 获取全局推送配置
 */
export function getPushConfig(): Promise<PushConfigVO> {
  return request.get<PushConfigVO>(`${BASE_URL}/config`)
}

/**
 * 保存全局推送配置
 */
export function savePushConfig(data: PushConfigVO): Promise<void> {
  return request.post<void>(`${BASE_URL}/config`, data)
}

/**
 * 获取导出模板可选字段
 */
export function getExportFields(): Promise<string[]> {
  return request.get<string[]>(`${BASE_URL}/export-fields`)
}

/**
 * 今日运价分页查询
 */
export function pageDailyRates(params: DailyRateQueryPageRequest): Promise<DailyRatePageResponse> {
  return request.post<DailyRatePageResponse>(`${BASE_URL}/daily/page`, params)
}

/**
 * 今日运价导出
 */
export function exportDailyRates(exportFieldKeys?: string[]): Promise<{ fileId: string; fileName: string }> {
  return request.post<{ fileId: string; fileName: string }>(`${BASE_URL}/daily/export`, exportFieldKeys)
}

/** Excel 解析校验行 */
export interface SubscriptionParseRow {
  rowNum: number
  polCode?: string
  podCode?: string
  carrierCodes?: string
  containerTypes?: string
  status: 'success' | 'failed'
  errorMsg?: string
}

/** Excel 解析校验结果 */
export interface SubscriptionParseResult {
  total: number
  success: number
  failed: number
  details: SubscriptionParseRow[]
}

/**
 * 解析并校验导入订阅的 Excel 文件
 */
export function parseSubscriptions(file: File): Promise<SubscriptionParseResult> {
  const formData = new FormData()
  formData.append('file', file)
  return request.post<SubscriptionParseResult>(`${BASE_URL}/subscriptions/parse`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

